import {Center, createStyles, Loader} from '@mantine/core';
import React, {useState} from 'react';

import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface Props {
  /** URL the browser can fetch as an image (typically a signed URL returning PNG or SVG). */
  src: string;
  /** Accessible label. Also used for the error placeholder's aria-label. */
  alt?: string;
}

const useStyles = createStyles((theme) => ({
  errorIcon: {
    marginRight: theme.spacing.xs,
  },
  wrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  imageHidden: {
    visibility: 'hidden',
  },
  loaderOverlay: {
    position: 'absolute',
    inset: 0,
  },
}));

/**
 * Renders a Reaction Scheme as a Thumbnail. Specifically used for listing Reactions in Search.
 */
export default function ReactionSchemeThumbnail({src, alt = 'Reaction scheme'}: Props): JSX.Element {
  const {classes, cx} = useStyles();
  const [status, setStatus] = useState<AsyncStatus>(AsyncStatus.LOADING);

  if (status === AsyncStatus.ERROR) {
    return (
      <Center h="100%" w="100%" role="img" aria-label={`${alt} unavailable`}>
        <BenchlingIcon icon="image" className={cx('faded', classes.errorIcon)} /> Cannot load Reaction scheme
      </Center>
    );
  }

  return (
    <div className={classes.wrapper}>
      <img
        src={src}
        alt={alt}
        onLoad={() => {
          setStatus(AsyncStatus.SUCCESS);
        }}
        onError={() => {
          setStatus(AsyncStatus.ERROR);
        }}
        // hide the default broken image browser fallback when not loading or unsuccessful
        className={cx(classes.image, {[classes.imageHidden]: status !== AsyncStatus.SUCCESS})}
      />
      {status === AsyncStatus.LOADING && (
        <Center className={classes.loaderOverlay}>
          <Loader size="sm" />
        </Center>
      )}
    </div>
  );
}
